/**
 * Subscription Plans 状态管理 Store
 *
 * 缓存 `/subscriptions/plans` 返回的订阅方案列表。该列表在前端是只读的，
 * 任何用户操作（包括 subscribe / cancel）都不会改变 plans 本身，所以这里
 * 走全局缓存：登录态进入 Layout 时拉一次，后续页面（Recharge 等）直接复用。
 */

import { create } from 'zustand';
import { getSubscriptionPlans, type SubscriptionPlan } from '@/api';

/**
 * Plans 状态接口
 */
export interface PlansState {
  // ==================== 状态 ====================

  /** 订阅方案列表 */
  plans: SubscriptionPlan[];
  /** 是否正在请求 /subscriptions/plans */
  isLoadingPlans: boolean;
  /** 是否已成功拉取过一次 plans */
  plansLoaded: boolean;
  /** 最后一次成功更新的时间戳（毫秒） */
  lastUpdated: number | null;

  // ==================== Actions ====================

  /** 拉取 /subscriptions/plans 并更新缓存 */
  refreshPlans: () => Promise<void>;

  /** 清空所有 plans 数据（一般无需调用） */
  clear: () => void;
}

const initialState = {
  plans: [] as SubscriptionPlan[],
  isLoadingPlans: false,
  plansLoaded: false,
  lastUpdated: null as number | null,
};

/**
 * Subscription Plans Store
 *
 * 仅内存缓存接口数据，刷新页面后必须重新请求，避免展示过期业务数据。
 */
export const usePlansStore = create<PlansState>()((set) => ({
  ...initialState,

  refreshPlans: async () => {
    set({ isLoadingPlans: true });
    try {
      const res = await getSubscriptionPlans();
      if (res.ok) {
        set({
          plans: res.data,
          plansLoaded: true,
          lastUpdated: Date.now(),
        });
      } else {
        console.error('[Plans Store] getSubscriptionPlans failed:', res.error);
      }
    } catch (error) {
      console.error('[Plans Store] getSubscriptionPlans error:', error);
    } finally {
      set({ isLoadingPlans: false });
    }
  },

  clear: () => {
    set(initialState);
  },
}));

/**
 * 便捷 Hooks
 */

/** 订阅方案列表 */
export const useSubscriptionPlansList = () =>
  usePlansStore((state) => state.plans);

/** plans 是否正在加载 */
export const useSubscriptionPlansLoading = () =>
  usePlansStore((state) => state.isLoadingPlans);

/** plans 是否已成功拉取过一次 */
export const useSubscriptionPlansLoaded = () =>
  usePlansStore((state) => state.plansLoaded);

/** 获取 refreshPlans action */
export const useRefreshSubscriptionPlans = () =>
  usePlansStore((state) => state.refreshPlans);
